/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, Plus, LogOut, Users as UsersIcon, X } from "lucide-react";

export default function CollegialesPage() {
  const { user } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [collegiales, setCollegiales] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([
        api.get("/leaderboard"),
        api.get("/collegiales"),
      ]);
      setLeaderboard(l.data);
      setCollegiales(c.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const myCol = collegiales.find(c => c.is_member);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/collegiales", { name, description: desc });
      toast.success(`Collégiale « ${name} » fondée !`);
      setShowCreate(false); setName(""); setDesc("");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const join = async (id) => {
    try { await api.post(`/collegiales/${id}/join`); toast.success("Tu rejoins la Collégiale !"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const leave = async (id) => {
    if (!window.confirm("Quitter cette Collégiale ?")) return;
    try { await api.post(`/collegiales/${id}/leave`); toast.success("Tu as quitté la Collégiale."); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="collegiales-page">
      <div>
        <div className="overline">Réseau professoral</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-none mt-1 mb-4">Collégiales</h1>
      </div>

      {/* Leaderboard */}
      <section className="mb-6" data-testid="leaderboard">
        <div className="flex justify-between items-end mb-2 border-b border-[#2B2B2B] pb-1">
          <h2 className="font-serif-display text-2xl flex items-center gap-2"><Trophy size={18}/> Classement des professeurs</h2>
          <span className="font-mono text-xs text-[#4A4A4A]">Score = Pokédex×3 + Badges×2 + Maîtres×10 + Légendaires×5 + Shinies×8</span>
        </div>
        <div className="panel p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#EAE4D9]">
              <tr>
                <th className="text-left px-3 py-2 overline">#</th>
                <th className="text-left px-3 py-2 overline">Professeur</th>
                <th className="text-right px-3 py-2 overline">Pokédex</th>
                <th className="text-right px-3 py-2 overline hidden md:table-cell">Badges</th>
                <th className="text-right px-3 py-2 overline hidden md:table-cell">Maîtres</th>
                <th className="text-right px-3 py-2 overline hidden lg:table-cell">Lég.</th>
                <th className="text-right px-3 py-2 overline hidden lg:table-cell">Shinies</th>
                <th className="text-right px-3 py-2 overline">Score</th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((row, i) => (
                <tr
                  key={row.user_id}
                  className={`border-t border-[#2B2B2B] ${row.is_me ? "bg-[#FCF9F2] font-bold" : ""}`}
                  data-testid={`leaderboard-row-${i}`}
                >
                  <td className="px-3 py-1.5 font-mono">{i + 1}</td>
                  <td className="px-3 py-1.5 font-serif-display text-base">
                    {row.professor_name}{row.is_me && <span className="ml-2 stamp text-[8px]">VOUS</span>}
                  </td>
                  <td className="text-right px-3 py-1.5 font-mono">{row.pokedex_caught}</td>
                  <td className="text-right px-3 py-1.5 font-mono hidden md:table-cell">{row.badges_total}</td>
                  <td className="text-right px-3 py-1.5 font-mono hidden md:table-cell">{row.league_winners}</td>
                  <td className="text-right px-3 py-1.5 font-mono hidden lg:table-cell">{row.legendaries}</td>
                  <td className="text-right px-3 py-1.5 font-mono hidden lg:table-cell">{row.shinies}</td>
                  <td className="text-right px-3 py-1.5 font-mono text-[#8C2A2A]">{row.score}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* My collegiale */}
      <section className="mb-6">
        <div className="flex justify-between items-end mb-2 border-b border-[#2B2B2B] pb-1">
          <h2 className="font-serif-display text-2xl flex items-center gap-2"><UsersIcon size={18}/> Collégiales</h2>
          {!myCol && (
            <button data-testid="collegiale-create-btn" className="btn-ink btn-leaf" onClick={() => setShowCreate(true)}>
              <Plus size={12}/> Fonder une Collégiale
            </button>
          )}
        </div>

        {collegiales.length === 0 ? (
          <div className="panel p-8 text-center text-[#7A7A7A] font-mono text-sm">
            Aucune Collégiale fondée pour l&apos;instant. Sois le premier !
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {collegiales.map((c) => (
              <div key={c.id} className={`panel p-3 ${c.is_member ? "border-[#2E4F32] border-2" : ""}`} data-testid={`collegiale-${c.id}`}>
                <div className="flex justify-between items-start gap-2">
                  <div>
                    <h3 className="font-serif-display text-xl leading-tight">{c.name}</h3>
                    <div className="text-[10px] font-mono text-[#4A4A4A]">
                      {c.members_count} membre{c.members_count > 1 ? "s" : ""} · score {c.total_score}
                    </div>
                  </div>
                  {c.is_member ? (
                    <button data-testid={`leave-${c.id}`} className="btn-ink btn-accent text-[10px] py-1 px-2" onClick={() => leave(c.id)}>
                      <LogOut size={10}/> Quitter
                    </button>
                  ) : (
                    <button data-testid={`join-${c.id}`} className="btn-ink btn-leaf text-[10px] py-1 px-2" disabled={!!myCol} onClick={() => join(c.id)}>
                      Rejoindre
                    </button>
                  )}
                </div>
                {c.description && <p className="text-xs text-[#4A4A4A] mt-1">{c.description}</p>}
                <ul className="mt-2 text-[11px] font-mono">
                  {c.members.slice(0, 5).map((m) => (
                    <li key={m.user_id} className="flex justify-between border-t border-[#EAE4D9] py-0.5">
                      <span>{m.professor_name}{m.user_id === user.id && " (vous)"}</span>
                      <span className="text-[#4A4A4A]">{m.score} pts · {m.pokedex_caught} dex</span>
                    </li>
                  ))}
                  {c.members.length > 5 && <li className="text-[#7A7A7A]">…et {c.members.length - 5} autre(s)</li>}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {showCreate && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ background: "rgba(26,26,26,0.55)", zIndex: 70 }}
          onClick={() => setShowCreate(false)}
        >
          <form onSubmit={create} className="panel-strong p-5 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="absolute top-3 right-3 btn-ink p-1 px-1.5" onClick={() => setShowCreate(false)}>
              <X size={14}/>
            </button>
            <div className="overline">Nouvelle Collégiale</div>
            <h3 className="font-serif-display text-2xl leading-none mt-1 mb-4">Fonder une Collégiale</h3>
            <div className="mb-3">
              <label className="overline block mb-1">Nom</label>
              <input
                data-testid="collegiale-name-input"
                className="ink-input" required minLength={2} maxLength={40}
                value={name} onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="overline block mb-1">Description (optionnel)</label>
              <textarea
                data-testid="collegiale-desc-input"
                className="ink-input" maxLength={200} rows={3}
                value={desc} onChange={(e) => setDesc(e.target.value)}
              />
            </div>
            <button
              data-testid="collegiale-create-submit"
              type="submit"
              disabled={busy}
              className="btn-ink btn-leaf w-full justify-center"
            >
              {busy ? "Création..." : "Fonder"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
